import { i18n } from '@lingui/core';
import type {
    FilterItem,
    StandardFilter,
} from '@schibsted-nmp/search-frontend-clients/quest';
import React from 'react';
import { useFilterSearch } from '../useFilterSearch';
import { MultiSelectCategoryItems } from './MultiSelectCategoryItems';
import { ParentCategoryButton } from './ParentCategoryButton';
import { SingleSelectCategoryItems } from './SingleSelectCategoryItems';

type CategoryFilterProps = {
    filter: StandardFilter;
};

export function CategoryFilter(props: CategoryFilterProps) {
    const { filter } = props;

    const { addToPath, removeFromPath, selectedCategoryPath } =
        useSelectedCategoryPath(filter);

    const isChildCategory = selectedCategoryPath.length > 1;
    const isSubCategory = selectedCategoryPath.length === 3;

    const selectedCategory =
        selectedCategoryPath.length >= 1 &&
        selectedCategoryPath[-1 + selectedCategoryPath.length];
    const parentCategory =
        selectedCategoryPath.length >= 2 &&
        selectedCategoryPath[-2 + selectedCategoryPath.length];

    const { submitFilterSearch } = useFilterSearch();

    const handleParentCategoryClick = React.useCallback(() => {
        if (selectedCategory && parentCategory) {
            removeFromPath();
            isTopLevelCategory(parentCategory)
                ? submitFilterSearch(selectedCategory)
                : submitFilterSearch(selectedCategory, parentCategory);
        }
    }, [parentCategory, removeFromPath, selectedCategory, submitFilterSearch]);

    const handleFilterSelect = React.useCallback(
        (filterItem: FilterItem, parentItem?: FilterItem) => {
            addToPath(filterItem);
            submitFilterSearch(filterItem, parentItem);
        },
        [addToPath, submitFilterSearch],
    );

    if (!selectedCategory) {
        return null;
    }

    return (
        <div>
            {parentCategory && (
                <>
                    <ParentCategoryButton
                        className="mb-10"
                        label={parentCategory.display_name}
                        onClick={handleParentCategoryClick}
                    />
                    <div className="mb-6">
                        {selectedCategory.display_name}
                        {selectedCategory.filter_items?.length === 0 && (
                            <>
                                {
                                    ' ' /* Add a space between the name and the hits */
                                }
                                <span className="text-s s-text-subtle">
                                    ({selectedCategory.hits})
                                </span>
                            </>
                        )}
                    </div>
                </>
            )}
            <div
                // Indent the items if this is a subcategory
                className={`${isChildCategory ? 'ml-20' : ''}`}
            >
                {isSubCategory ? (
                    <MultiSelectCategoryItems
                        onSelect={handleFilterSelect}
                        parentItem={selectedCategory}
                    />
                ) : (
                    <SingleSelectCategoryItems
                        onSelect={handleFilterSelect}
                        parentItem={selectedCategory}
                    />
                )}
            </div>
        </div>
    );
}

function useSelectedCategoryPath(filter: StandardFilter) {
    const [selectedCategoryPath, setSelectedCategoryPath] = React.useState(() =>
        findSelectedCategoryPath(filter),
    );

    React.useEffect(
        function syncFilterState() {
            setSelectedCategoryPath(findSelectedCategoryPath(filter));
        },
        [filter],
    );

    const addToPath = React.useCallback((filterItem: FilterItem) => {
        setSelectedCategoryPath((path) => {
            const isSubCategory = path.length === 3;
            if (isSubCategory) {
                return path;
            }
            const updatedFilterItem = {
                ...filterItem,
                selected: true,
            };
            return [...path, updatedFilterItem];
        });
    }, []);

    const removeFromPath = React.useCallback(() => {
        setSelectedCategoryPath((path) => path.slice(0, -1));
    }, []);

    return { addToPath, removeFromPath, selectedCategoryPath };
}

function findSelectedCategoryPath(filter: StandardFilter): FilterItem[] {
    const selectedCategoryPath: FilterItem[] = [];

    const topLevelCategory = createTopLevelCategory(filter);
    let currentItem: FilterItem | undefined = topLevelCategory;
    let currentLevel = 1;

    while (currentItem && currentLevel <= 3) {
        selectedCategoryPath.push(currentItem);
        currentItem = currentItem.filter_items?.find((item) => item.selected);
        if (currentItem) {
            currentLevel++;
        }
    }

    return selectedCategoryPath;
}

const TOP_LEVEL_CATEGORY_ATTRIBUTES = {
    hits: -1,
    name: '',
    selected: false,
    value: '',
};

function createTopLevelCategory(filter: StandardFilter): FilterItem {
    return {
        display_name: i18n.t({
            id: 'filters.category.all-categories',
            message: 'All Categories',
        }),
        filter_items: filter.filter_items,
        ...TOP_LEVEL_CATEGORY_ATTRIBUTES,
    };
}

function isTopLevelCategory(filterItem: FilterItem) {
    return (
        filterItem.hits === TOP_LEVEL_CATEGORY_ATTRIBUTES.hits &&
        filterItem.selected === TOP_LEVEL_CATEGORY_ATTRIBUTES.selected
    );
}
